import React, { useRef, useEffect, type FC, startTransition } from 'react';
import type { TransportData } from '@/domainLogic/index.ts';
import { type Dates, DatesType, DateType } from '@/app/components/search/utils';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { ClickEventNameEnum } from '@/app/components/search/Curiosity/Analytics/utils/ClickEventNameEnum';
import type { ClickParams } from '@/app/components/search/Curiosity/Analytics/utils';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { toYYYYMMDD } from '@/app/components/search/utils/misc';
import { addDays } from 'date-fns';
import { convertDate, DecorateDates, DecorateDatesHover, getContainerEl, resetHoverDates } from '../utils';
import Months from './Months';
import { DatePickerStyled } from './styled';

export type DatePickerProps = {
  month: number;
  year: number;
  dates?: Dates;
  setDates: React.Dispatch<React.SetStateAction<Dates | undefined>>;
  setMonth: React.Dispatch<React.SetStateAction<number>>;
  setYear: React.Dispatch<React.SetStateAction<number>>;
  name: string;
  setSelectedInput: React.Dispatch<React.SetStateAction<number>>;
  selectedInput: number;
  hasDates: boolean;
  eventCallback: (payload: ClickParams) => void;
  rangeDates?: { startDate: string; endDate: string };
  disableDatesOutsideBookingWindow: boolean;
  transportData: TransportData[] | undefined;
  getCalendarHeight?: (fixedHeadersHeight: number) => string;
  returnDateWasPreselected?: boolean;
  setReturnDateWasPreselected?: React.Dispatch<React.SetStateAction<boolean>>;
};

enum SELECTED_INPUT {
  DEPARTURE = 1,
  RETURN = 2,
  FLEXIBLE = 3,
}

export const DatePicker: FC<DatePickerProps> = ({
  month,
  year,
  dates,
  setDates,
  setMonth,
  setYear,
  name,
  setSelectedInput,
  selectedInput,
  hasDates,
  eventCallback,
  disableDatesOutsideBookingWindow,
  transportData,
  getCalendarHeight,
  returnDateWasPreselected,
  setReturnDateWasPreselected,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useCommonClient();

  const isDepartureSelected = selectedInput === SELECTED_INPUT.DEPARTURE;
  const isReturnSelected = selectedInput === SELECTED_INPUT.RETURN;
  const isFlexibleDateSelected = selectedInput === SELECTED_INPUT.FLEXIBLE;

  const hasDepartureDate = !!dates?.departureDate?.date;
  const hasReturnDate = !!dates?.returnDate?.date;
  const hasDepartureDateAndReturnDate = hasDepartureDate && hasReturnDate;

  const isSelectingDepartureDate =
    isDepartureSelected ||
    isFlexibleDateSelected ||
    (isReturnSelected && hasDepartureDateAndReturnDate && !returnDateWasPreselected);

  const isSelectingReturnDate =
    (hasDepartureDate && isReturnSelected && !hasReturnDate) ||
    (hasDepartureDate && isReturnSelected && returnDateWasPreselected);

  const showHoverMouseOver = isSelectingReturnDate && !isMobile;

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (containerRef?.current) {
      const allItems = [...containerRef.current.querySelectorAll('[data-date]')];
      resetHoverDates(allItems);
    }
  };

  const handleMouseOver = (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const { hoverDate } = getContainerEl(e);

    startTransition(() => {
      const startDate = dates?.departureDate?.date;
      const endDate = hoverDate;

      if (startDate && endDate) {
        DecorateDatesHover({ containerRef, startDate, endDate });
      }
    });
  };

  const handleDateSelection = (hoverDate: string): void => {
    const commonDates = dates
      ? {
          type: DatesType.CALENDAR,
          flexDate: dates.flexDate,
          subDate: dates.subDate,
        }
      : null;

    if (isSelectingDepartureDate) {
      const nextDay = toYYYYMMDD(addDays(new Date(hoverDate), 1));

      if (window.innerWidth < 768) {
        DecorateDates({ containerRef, startDate: hoverDate, endDate: '' });
      }

      eventCallback({
        event_name: ClickEventNameEnum.SearchwidgetCalendarDepartureDateSelection,
        event_subcontext: 'input',
        event_value: hoverDate,
      });

      setTimeout(() => {
        setSelectedInput(() => 2);
        if (setReturnDateWasPreselected) setReturnDateWasPreselected(true);
        if (commonDates)
          setDates({
            departureDate: {
              date: hoverDate,
              type: DateType.EXACT,
            },
            returnDate: {
              date: nextDay,
              type: DateType.EXACT,
            },
            ...commonDates,
          });
      }, 50);

      return;
    }

    if (isSelectingReturnDate) {
      const isHoverLessThanDepartureDate =
        dates.departureDate.date && convertDate(hoverDate) <= convertDate(dates.departureDate.date);
      const startDate = isHoverLessThanDepartureDate ? hoverDate : dates.departureDate.date;
      const endDate = isHoverLessThanDepartureDate ? dates.departureDate.date : hoverDate;

      eventCallback({
        event_name: isHoverLessThanDepartureDate
          ? ClickEventNameEnum.SearchwidgetCalendarDepartureDateSelection
          : ClickEventNameEnum.SearchwidgetCalendarReturnDateSelection,
        event_subcontext: 'input',
        event_value: hoverDate,
      });

      if (window.innerWidth < 768) {
        DecorateDates({ containerRef, startDate, endDate });
      }

      setTimeout(() => {
        if (startDate === endDate) return;

        if (commonDates) {
          if (setReturnDateWasPreselected) setReturnDateWasPreselected(false);
          setDates({
            departureDate: {
              date: startDate,
              type: DateType.EXACT,
            },
            returnDate: {
              date: endDate,
              type: DateType.EXACT,
            },
            ...commonDates,
          });
        }
      }, 100);
    }
  };

  const handleMouseDown = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>
  ): void => {
    startTransition(() => {
      e.stopPropagation();

      if (
        (e as React.MouseEvent<HTMLDivElement, MouseEvent>).button &&
        (e as React.MouseEvent<HTMLDivElement, MouseEvent>).button === 2
      ) {
        return;
      }
      const { hoverDate, shouldReturn } = getContainerEl(e);

      if (hoverDate && !shouldReturn) {
        handleDateSelection(hoverDate);
      }
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    if (e.detail !== 0) {
      return;
    }

    startTransition(() => {
      e.stopPropagation();

      const { hoverDate, shouldReturn } = getContainerEl(e);

      if (hoverDate && !shouldReturn) {
        handleDateSelection(hoverDate);
      }
    });
  };

  const monthsProps = {
    month,
    year,
    dates,
    setMonth,
    setYear,
    setDates,
    i: 0,
    name,
    containerRef,
    isMobile,
    eventCallback,
    disableDatesOutsideBookingWindow,
    transportData,
  };

  useEffect(() => {
    if (containerRef.current && canUseDOM && isMobile) {
      containerRef.current.style.height = getCalendarHeight
        ? getCalendarHeight(hasDates ? 83 + 130 : 83 + 84)
        : `${hasDates ? window.innerHeight - (83 + 130) : window.innerHeight - (83 + 84)}px`;
    }
  }, [hasDates, isMobile]);

  return (
    <DatePickerStyled
      ref={containerRef}
      {...{
        onMouseDown: (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => handleMouseDown(e),
        onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => handleClick(e),
        onMouseOver: (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void =>
          showHoverMouseOver ? handleMouseOver(e) : undefined,
        onMouseLeave: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          showHoverMouseOver ? handleMouseLeave(e) : undefined,
        className: 'oss-ui-calendar',
      }}
    >
      <Months {...monthsProps} />
    </DatePickerStyled>
  );
};
